import { useStores } from '@littlebird/sdk';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';

import { backendApis } from '@/backendApis';
import { electronApis } from '@/features/electron-api';
import { trackEvent } from '@/utils/analytics';

import {
  notifyFirstRunReady,
  seedFirstRunSummaryChat,
} from './first-run-summary.actions';
import {
  type FirstRunDevtools,
  useFirstRunDevtools,
} from './first-run-summary.devtools';
import {
  FIRST_RUN_WINDOW_MS,
  firstRunSummaryStore,
} from './first-run-summary.store';

const SUMMARY_POLL_INTERVAL_MS = 5000;

// Drives the post-onboarding first-run summary (poll → seed chat → coachmark).
// Call once from an observer host (ContextCollectionProvider) so the store
// reads below stay reactive.
export function useFirstRunSummary(isCollecting: boolean): void {
  const { threads } = useStores();

  const { cutoffDate, phase, summary } = firstRunSummaryStore;
  const isLearning = phase === 'learning';
  const hasSummary = summary !== null;
  const cutoffMs = cutoffDate ? new Date(cutoffDate).getTime() : null;
  const withinWindow =
    cutoffMs !== null && Date.now() - cutoffMs < FIRST_RUN_WINDOW_MS;

  const { data: polled } = useQuery({
    queryKey: ['first-run-context-summary', cutoffDate],
    queryFn: () => backendApis.inputs.getRecentSummary(new Date(cutoffMs!)),
    enabled: isLearning && isCollecting && !hasSummary && withinWindow,
    refetchInterval: SUMMARY_POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
  });

  // Summary arrived → claim the seed slot, seed a chat from it, then notify.
  useEffect(() => {
    const summaryText = polled?.summary;
    if (
      !summaryText ||
      cutoffDate === null ||
      !firstRunSummaryStore.onSummaryArrived(cutoffDate, summaryText)
    ) {
      return;
    }

    trackEvent('ONBOARDING_CONTEXT_FIRST_RUN_SUMMARY_READY', {
      time_to_ready_seconds: cutoffMs
        ? Math.round((Date.now() - cutoffMs) / 1000)
        : null,
    });

    const suggestedRoutineSlugs = polled?.suggestedRoutineSlugs ?? [];
    void (async () => {
      try {
        const chatId = await seedFirstRunSummaryChat({
          threads,
          text: summaryText,
          suggestedRoutineSlugs,
        });
        notifyFirstRunReady(chatId);
      } catch (err) {
        // On failure fall back to the visible "nothing yet" state, not a dead 'done'.
        console.error('Failed to create first-run summary chat:', err);
        firstRunSummaryStore.markEmpty();
      }
    })();
  }, [polled, cutoffDate, cutoffMs, threads]);

  // Give up quietly when the window elapses without a summary (nothing to show)
  useEffect(() => {
    if (!isLearning || cutoffMs === null) return;

    const remaining = FIRST_RUN_WINDOW_MS - (Date.now() - cutoffMs);
    const giveUp = () => {
      if (firstRunSummaryStore.onWindowElapsed()) {
        trackEvent('ONBOARDING_CONTEXT_FIRST_RUN_GAVE_UP');
      }
    };

    if (remaining <= 0) {
      giveUp();
      return;
    }
    const timer = setTimeout(giveUp, remaining);
    return () => clearTimeout(timer);
  }, [isLearning, cutoffMs, hasSummary]);

  // Live readout: stream captured app names into the pill while learning
  useEffect(() => {
    if (!isLearning) return;

    electronApis.context.enableDebugStream();
    const unlisten = electronApis.context.listenForDebugSnapshotReport(
      (report) => {
        if (report.outcome === 'success') {
          firstRunSummaryStore.pushCapture(report.appName);
        }
      }
    );

    return () => {
      unlisten();
      electronApis.context.disableDebugStream();
    };
  }, [isLearning]);

  // Dev-only: window.__lbFirstRun to re-test the flow without new accounts
  const devtools = useMemo<FirstRunDevtools>(
    () => ({
      restart: () => {
        firstRunSummaryStore.reset();
        firstRunSummaryStore.beginFirstRun(new Date().toISOString());
      },
      reset: () => firstRunSummaryStore.reset(),
      capture: (app: string) => firstRunSummaryStore.pushCapture(app),
      forceReady: async (text?: string) => {
        if (firstRunSummaryStore.phase !== 'learning') {
          firstRunSummaryStore.beginFirstRun(new Date().toISOString());
        }
        const summaryText =
          text ??
          'Sample first-run summary — you spent time across your editor, email, and browser.';
        firstRunSummaryStore.setSummary(summaryText);
        await seedFirstRunSummaryChat({ threads, text: summaryText });
      },
    }),
    [threads]
  );
  useFirstRunDevtools(devtools);
}
